import { useState, useEffect, useCallback } from "react";
import "./RequestView.css";
import MobileFrame from "../MobileFrame";
import { MOCK_CALLS, CallItem, RepairShop, FaultCode, formatCost, formatTime } from "../repairData";

interface DiagnosisResult { fault: FaultCode; vehicleNumber: string; }

interface RequestViewProps {
    diagnosisResult: DiagnosisResult | null;
    selectedShop: RepairShop | null;
    onComplete: () => void;
}

type ModeTab = "driver" | "shop";
type ReqState = "idle" | "waiting" | "accepted";

// ── Call Timer ────────────────────────────────────────────────────────────
function CallTimer({ init, onExpire }: { init: number; onExpire: () => void }) {
    const [sec, setSec] = useState(init);
    useEffect(() => {
        if (sec <= 0) { onExpire(); return; }
        const t = setTimeout(() => setSec(s => s - 1), 1000);
        return () => clearTimeout(t);
    }, [sec, onExpire]);
    return <span className={`web-call-timer ${sec <= 15 ? "urgent" : ""}`}>{sec}초</span>;
}

// ── Mobile: Driver App Content ────────────────────────────────────────────
function MobileDriverContent({
    diagnosisResult, selectedShop, reqState, requestTime, acceptedTime,
    onSubmit, onComplete,
}: {
    diagnosisResult: DiagnosisResult | null;
    selectedShop: RepairShop | null;
    reqState: ReqState;
    requestTime: string;
    acceptedTime: string;
    onSubmit: () => void;
    onComplete: () => void;
}) {
    const hasInfo = diagnosisResult && selectedShop;

    return (
        <>
            <div className="mapp-header gradient">
                <div className="mapp-header-text">
                    <div className="mapp-header-title">수리 요청</div>
                    <div className="mapp-header-sub">정비소에 수리 요청을 전송합니다</div>
                </div>
                <div className="mapp-logo-badge">STEP 6</div>
            </div>

            <div style={{ flex: 1, overflow: "auto" }}>
                {/* Summary */}
                <div className="mapp-card" style={{ margin: "10px 10px 0" }}>
                    <div className="mapp-card-header">요청 정보</div>
                    <div className="mapp-row">
                        <span className="mapp-row-label">차량번호</span>
                        <span className="mapp-row-value">{diagnosisResult?.vehicleNumber || "—"}</span>
                    </div>
                    <div className="mapp-row">
                        <span className="mapp-row-label">고장 유형</span>
                        <span className={`mapp-row-value ${diagnosisResult?.fault.severity === "위험" ? "danger" : ""}`}>
                            {diagnosisResult?.fault.lightName || "—"}
                        </span>
                    </div>
                    <div className="mapp-row">
                        <span className="mapp-row-label">고장 코드</span>
                        <span className="mapp-row-value cyan" style={{ fontFamily: "monospace" }}>{diagnosisResult?.fault.code || "—"}</span>
                    </div>
                </div>

                <div className="mapp-card" style={{ margin: "8px 10px 0" }}>
                    <div className="mapp-card-header">선택 정비소</div>
                    <div className="mapp-row">
                        <span className="mapp-row-label">업체명</span>
                        <span className="mapp-row-value" style={{ fontSize: 12 }}>{selectedShop?.name || "—"}</span>
                    </div>
                    <div className="mapp-row">
                        <span className="mapp-row-label">예상 시간</span>
                        <span className="mapp-row-value">{selectedShop ? formatTime(selectedShop.estimatedTime) : "—"}</span>
                    </div>
                    <div className="mapp-row">
                        <span className="mapp-row-label">예상 비용</span>
                        <span className="mapp-row-value cyan">{selectedShop ? formatCost(selectedShop.estimatedCost) : "—"}</span>
                    </div>
                    <div className="mapp-row">
                        <span className="mapp-row-label">거리</span>
                        <span className="mapp-row-value">{selectedShop ? `${selectedShop.distance}km` : "—"}</span>
                    </div>
                </div>

                {/* Status timeline after submission */}
                {reqState !== "idle" && (
                    <div className="mapp-card" style={{ margin: "8px 10px 0" }}>
                        <div className="mapp-card-header" style={{ color: reqState === "accepted" ? "#0d9488" : "#0891b2" }}>
                            {reqState === "accepted" ? "✓ 수락 완료" : "전송 중..."}
                        </div>
                        <div className="mapp-timeline" style={{ padding: "12px" }}>
                            {[
                                { label: "수리 요청 전송", time: requestTime, st: "done" as const },
                                { label: reqState === "accepted" ? `${selectedShop?.name} 수락` : "정비소 응답 대기중", time: reqState === "accepted" ? acceptedTime : "처리 중...", st: (reqState === "accepted" ? "done" : "active") as "done" | "active" | "pending" },
                                { label: "정비소 이동", time: reqState === "accepted" ? `예상 ${formatTime(selectedShop?.estimatedTime || 0)}` : "대기", st: (reqState === "accepted" ? "active" : "pending") as "done" | "active" | "pending" },
                                { label: "정비 완료", time: "대기", st: "pending" as const },
                            ].map((item, i) => (
                                <div key={i} className={`mapp-tl-item ${item.st}`}>
                                    <div className={`mapp-tl-dot ${item.st}`}>
                                        {item.st === "done" && (
                                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                        )}
                                    </div>
                                    <div className="mapp-tl-text">
                                        <span className="mapp-tl-label">{item.label}</span>
                                        <span className="mapp-tl-time">{item.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!hasInfo && (
                    <div style={{ margin: "10px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 10, padding: "12px", fontSize: 12, color: "#92400e", textAlign: "center", lineHeight: 1.6 }}>
                        고장진단 → 업체추천 순서로<br />먼저 진행해 주세요
                    </div>
                )}
            </div>

            <div className="mapp-cta">
                {reqState === "idle" ? (
                    <button className="mapp-cta-btn" disabled={!hasInfo} onClick={onSubmit}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                        정비소에 요청 보내기
                    </button>
                ) : reqState === "waiting" ? (
                    <button className="mapp-cta-btn" disabled style={{ background: "#0891b2" }}>
                        <div style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "white", borderRadius: "50%", animation: "mapp-spin 0.8s linear infinite" }} />
                        정비소 응답 대기 중...
                    </button>
                ) : (
                    <button className="mapp-cta-btn" style={{ background: "#0d9488" }} onClick={onComplete}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                        정비현황 확인하기
                    </button>
                )}
            </div>
        </>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function RequestView({ diagnosisResult, selectedShop, onComplete }: RequestViewProps) {
    const [activeMode, setActiveMode] = useState<ModeTab>("driver");
    const [reqState, setReqState] = useState<ReqState>("idle");
    const [requestTime, setRequestTime] = useState("");
    const [acceptedTime, setAcceptedTime] = useState("");
    const [calls, setCalls] = useState<CallItem[]>(MOCK_CALLS.map(c => ({ ...c })));
    const [acceptedIds, setAcceptedIds] = useState<string[]>([]);

    const handleExpire = useCallback((id: string) => {
        setCalls(prev => prev.filter(c => c.id !== id));
    }, []);

    const handleGrab = (id: string) => setAcceptedIds(prev => [...prev, id]);

    const handleSubmit = () => {
        const now = new Date();
        setRequestTime(now.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }));
        setReqState("waiting");
        setTimeout(() => {
            setAcceptedTime(new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }));
            setReqState("accepted");
        }, 3000);
    };

    const pending = calls.filter(c => !acceptedIds.includes(c.id));
    const accepted = calls.filter(c => acceptedIds.includes(c.id));

    const scenarioStep = activeMode === "driver"
        ? "STEP 6 — 정비소 전송 (고장 내역 전송·가격경쟁 유도)"
        : "STEP 6 — 정비소 콜 접수 (카카오택시형 하이재킹 시스템)";

    return (
        <div className="request-page">
            {/* ── Left: Mobile (Driver App) ── */}
            <div className="request-mobile-side">
                <MobileFrame label="운전자 앱 화면">
                    <MobileDriverContent
                        diagnosisResult={diagnosisResult}
                        selectedShop={selectedShop}
                        reqState={reqState}
                        requestTime={requestTime}
                        acceptedTime={acceptedTime}
                        onSubmit={handleSubmit}
                        onComplete={onComplete}
                    />
                </MobileFrame>
            </div>

            {/* ── Right: Web Side ── */}
            <div className="request-web-side">
                {/* Scenario banner */}
                <div className="req-scenario-banner">
                    <span className="req-scenario-label">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                        시나리오 — 비실시간 정적 대응형
                    </span>
                    <span className="req-active-step">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                        {scenarioStep}
                    </span>
                </div>

                {/* Mode Tabs */}
                <div className="req-mode-tabs">
                    <button className={`req-mode-tab ${activeMode === "driver" ? "active" : ""}`} onClick={() => setActiveMode("driver")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                        </svg>
                        운전자 요청 현황
                    </button>
                    <button className={`req-mode-tab ${activeMode === "shop" ? "active" : ""}`} onClick={() => setActiveMode("shop")}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                        정비소 콜 시스템
                    </button>
                </div>

                {/* ── Tab 1: Driver request web view ── */}
                {activeMode === "driver" && (
                    <>
                        <div className="notif-card">
                            <div className="notif-header">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                정비소 수신 정보 — {selectedShop?.name || "선택된 정비소 없음"}
                            </div>
                            <div className="notif-body">
                                <div className="notif-row">
                                    <span className="notif-label">요청 차량</span>
                                    <span className="notif-value">{diagnosisResult?.vehicleNumber || "—"}</span>
                                </div>
                                <div className="notif-row">
                                    <span className="notif-label">고장 유형</span>
                                    <span className={`notif-value ${diagnosisResult?.fault.severity === "위험" ? "danger" : ""}`}>
                                        {diagnosisResult?.fault.lightName || "—"}
                                    </span>
                                </div>
                                <div className="notif-row">
                                    <span className="notif-label">고장 코드</span>
                                    <span className="notif-value cyan" style={{ fontFamily: "monospace" }}>{diagnosisResult?.fault.code || "—"}</span>
                                </div>
                                <hr className="notif-divider" />
                                <div className="notif-row">
                                    <span className="notif-label">예상 수리 시간</span>
                                    <span className="notif-value">{selectedShop ? formatTime(selectedShop.estimatedTime) : "—"}</span>
                                </div>
                                <div className="notif-row">
                                    <span className="notif-label">견적 비용</span>
                                    <span className="notif-value">{selectedShop ? formatCost(selectedShop.estimatedCost) : "—"}</span>
                                </div>
                                <div className="notif-row">
                                    <span className="notif-label">운전자 거리</span>
                                    <span className="notif-value">{selectedShop ? `${selectedShop.distance}km` : "—"}</span>
                                </div>
                                <hr className="notif-divider" />
                                <div className="notif-row">
                                    <span className="notif-label">요청 상태</span>
                                    <span className={`notif-value ${reqState === "accepted" ? "cyan" : reqState === "waiting" ? "" : ""}`}
                                        style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        {reqState === "idle" && <span style={{ color: "#9ca3af" }}>대기 중</span>}
                                        {reqState === "waiting" && (
                                            <>
                                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#0ea5e9", animation: "live-pulse 1s ease-in-out infinite", display: "inline-block" }} />
                                                응답 대기 중
                                            </>
                                        )}
                                        {reqState === "accepted" && (
                                            <>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                <span style={{ color: "#0d9488" }}>수락 완료 ({acceptedTime})</span>
                                            </>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Guidance */}
                        <div className="notif-card">
                            <div className="notif-header">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                정비소 수요 예측 안내
                            </div>
                            <div className="notif-body" style={{ gap: 10 }}>
                                {["운전자가 요청을 보내는 즉시 선택된 정비소에 고장 내역이 전달됩니다.",
                                    "정비소는 차량 도착 예상 시간 내에 필요한 부품과 공간을 사전에 준비할 수 있습니다.",
                                    "가격 경쟁 입찰을 통해 운전자에게 최적 견적을 제시하면 재선택 기회가 생깁니다."].map((t, i) => (
                                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#374151" }}>
                                            <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#f1f5f9", color: "#475569", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</span>
                                            {t}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </>
                )}

                {/* ── Tab 2: Shop Call System (Web Browser) ── */}
                {activeMode === "shop" && (
                    <div className="browser-frame">
                        {/* Browser chrome */}
                        <div className="browser-chrome">
                            <div className="browser-dots">
                                <div className="browser-dot red" />
                                <div className="browser-dot yellow" />
                                <div className="browser-dot green" />
                            </div>
                            <div className="browser-addressbar">
                                <svg className="browser-lock" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                                <span className="browser-url">https://eview.im/repair/shop/calls?mro=78505566405&vin=KMFZSZ7NFPU04827654</span>
                            </div>
                        </div>

                        {/* Browser content */}
                        <div className="browser-content">
                            <div className="shop-web-header">
                                <span className="shop-web-title">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                    </svg>
                                    정비소 콜 접수 시스템
                                </span>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>부산 트럭 종합 정비 | 담당자: 김정비</span>
                            </div>
                            <div className="shop-web-body">
                                {/* Pending calls */}
                                <div>
                                    <div className="call-section-title">
                                        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                            </svg>
                                            수락 대기 중인 콜
                                        </span>
                                        {pending.length > 0 && (
                                            <div className="live-badge">
                                                <span className="live-dot" />
                                                {pending.length}건 LIVE
                                            </div>
                                        )}
                                    </div>

                                    {pending.length > 0 ? (
                                        <div className="web-call-list" style={{ marginTop: 10 }}>
                                            {pending.map(call => (
                                                <div key={call.id} className="web-call-card">
                                                    <div className="web-call-icon pending">
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                                                        </svg>
                                                    </div>
                                                    <div className="web-call-info">
                                                        <div className="web-call-vehicle">{call.vehicleNumber}</div>
                                                        <div className="web-call-meta">
                                                            <span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>{call.location} ({call.distance}km)</span>
                                                            <span style={{ color: "#6b7280" }}>{call.faultName}</span>
                                                        </div>
                                                    </div>
                                                    <div className="web-call-right">
                                                        <span className="web-call-cost">{formatCost(call.estimatedCost)}</span>
                                                        <CallTimer init={call.timerSeconds} onExpire={() => handleExpire(call.id)} />
                                                    </div>
                                                    <button className="web-grab-btn" onClick={() => handleGrab(call.id)}>콜 접수</button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ margin: "10px 0", background: "#f9fafb", border: "1px dashed #e2e8f0", borderRadius: 8, padding: "20px", textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
                                            현재 수락 가능한 콜이 없습니다
                                        </div>
                                    )}
                                </div>

                                {/* Accepted */}
                                {accepted.length > 0 && (
                                    <div>
                                        <div className="call-section-title">
                                            <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                                접수 완료
                                            </span>
                                            <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#f0fdf4", color: "#16a34a" }}>{accepted.length}건</span>
                                        </div>
                                        <div className="web-call-list" style={{ marginTop: 10 }}>
                                            {accepted.map(call => (
                                                <div key={call.id} className="web-call-card accepted">
                                                    <div className="web-call-icon accepted">
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    </div>
                                                    <div className="web-call-info">
                                                        <div className="web-call-vehicle">{call.vehicleNumber}</div>
                                                        <div className="web-call-meta">
                                                            <span>{call.location}</span>
                                                            <span>{call.faultName}</span>
                                                        </div>
                                                    </div>
                                                    <div className="web-call-right">
                                                        <span className="web-call-cost">{formatCost(call.estimatedCost)}</span>
                                                    </div>
                                                    <div className="web-accepted-label">
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                        접수됨
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
